// What KilatDomain pays the registry per TLD (screaming.md → pricing, `tld_costs`), shared by
// the API's pricing context and the staff SPA. Not what a reseller pays: that is a price list.
import { z } from "zod";
import { LABEL } from "./domain-name";
import { Money } from "./money";

/** Who registers a TLD automatically; the database CHECK reads the same list. */
export const REGISTRIES = ["pandi", "webnic"] as const;
export const Registry = z.enum(REGISTRIES);
export type Registry = z.infer<typeof Registry>;

/** The registry a TLD goes to unless staff choose otherwise: PANDI runs .id and below it. */
export function defaultRegistry(tld: string): Registry {
  return tld === "id" || tld.endsWith(".id") ? "pandi" : "webnic";
}

/**
 * Any TLD staff type: trimmed, lower-cased, without its leading dot.
 * Each label is a DNS label, so an IDN TLD is written as its punycode (xn--…).
 */
export const TldName = z
  .string()
  .transform((typed) => typed.trim().toLowerCase().replace(/^\./, ""))
  .refine((tld) => tld.length <= 253 && tld.split(".").every((label) => LABEL.test(label)), "invalid_tld");
export type TldName = z.infer<typeof TldName>;

/** The longest term a TLD is priced for, in years. */
export const MAX_YEARS = 10;

/** A key on the wire: 22 Base62 characters, never a raw UUID (screaming.md rule 19). */
const Base62Id = z.string().regex(/^[0-9A-Za-z]{22}$/);

/** One term of a TLD. A null price means that action is not offered for the term. */
export const TldCost = z
  .object({
    years: z.int().min(1).max(MAX_YEARS),
    enabled: z.boolean(),
    register: Money.nullable(),
    transfer: Money.nullable(),
    renew: Money.nullable(),
  })
  .superRefine((row, ctx) => {
    if (row.enabled && row.register === null) ctx.addIssue({ code: "custom", path: ["register"], message: "required" });
  });
export type TldCost = z.infer<typeof TldCost>;

/** Every term, 1 to MAX_YEARS in order, so a save replaces the whole grid. */
export const SetTldCostsRequest = z.object({
  costs: z
    .array(TldCost)
    .length(MAX_YEARS)
    .refine((rows) => rows.every((row, index) => row.years === index + 1), "terms_out_of_order"),
});
export type SetTldCostsRequest = z.infer<typeof SetTldCostsRequest>;

/** The grid of a TLD nobody has priced yet. */
export function emptyCosts(): TldCost[] {
  return Array.from({ length: MAX_YEARS }, (_, index) => ({
    years: index + 1,
    enabled: false,
    register: null,
    transfer: null,
    renew: null,
  }));
}

export const AddTldRequest = z.object({ tld: TldName, registry: Registry });
export type AddTldRequest = z.infer<typeof AddTldRequest>;

export const SetTldRegistryRequest = z.object({ registry: Registry });
export type SetTldRegistryRequest = z.infer<typeof SetTldRegistryRequest>;

/** Offer a TLD to resellers again, or withdraw it; a withdrawn TLD keeps everything set for it. */
export const SetTldOfferedRequest = z.object({ offered: z.boolean() });
export type SetTldOfferedRequest = z.infer<typeof SetTldOfferedRequest>;

export const MoveTldRequest = z.object({ direction: z.enum(["up", "down"]) });
export type MoveTldRequest = z.infer<typeof MoveTldRequest>;

/**
 * One row of the staff list, in display order. `costs` is empty until the TLD is priced. A TLD
 * is never removed, only withdrawn (`offered: false`): resellers stop seeing it, and what they set
 * for it is kept for when it is offered again.
 */
export const TldPricing = z.object({
  id: Base62Id,
  tld: TldName,
  registry: Registry,
  offered: z.boolean(),
  costs: z.array(TldCost),
});
export type TldPricing = z.infer<typeof TldPricing>;

export const ListTldPricingResponse = z.object({ data: z.array(TldPricing) });
export const TldPricingResponse = z.object({ data: TldPricing });
